/**
 * Cliente HTTP del motor A0 (principiante absoluto, docs/speak-plan/32-a0-engine.md F1b).
 *
 *   · GET  /a0/session       → la sesión de rondas que arma el selector SRS
 *     server-side ({ sessionId, support, rounds }). En F1b el backend emite
 *     flash/choice/repeat; el resto de los tipos llegan con su data en F3/F4.
 *   · POST /practice-runs    → write-back de la lección al SRS (`a0_item_state`).
 *     Mismo endpoint que Biblioteca (practice-store.ts): TODO juego puebla la
 *     maestría por ítem. El `game='a0-lesson'` NO acredita cobertura oral
 *     (sólo `decila` lo hace, D4) — el A0 de reconocimiento no mueve el meter.
 *
 * Patrón delgado de libApi (cookie httpOnly same-origin, envelope { success, data }).
 */

import { apiFetch } from './apiClient.js';

const BASE = '/api/modules/english';

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(`${BASE}${path}`, {
      ...(init?.body != null ? { headers: { 'content-type': 'application/json' } } : {}),
      ...init,
    });
  } catch {
    throw new Error('No pudimos conectar con el servidor.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { data?: T } & T;
  return json.data ?? json;
}

/** Andamiaje en español visible (derivado del `a0_stage`): high = ES siempre visible. */
export type A0Support = 'high' | 'low';

/** Los tipos del contrato (§13.1 + `present` = carta de presentación, pieza B). */
export type A0RoundType =
  | 'choice'
  | 'repeat'
  | 'listen'
  | 'match'
  | 'cloze'
  | 'wordbank'
  | 'present'
  | 'scene'
  | 'chat';

/** Ítem que viaja dentro de una ronda. `id` = texto EN normalizado → cruza con `a0_item_state.item_id`. */
export interface A0RoundItem {
  id: string;
  en: string;
  es: string;
  topic: string;
  /** Oración-ejemplo (EN) que usa el ítem — la muestra la carta de presentación. */
  example?: string;
  /** Traducción ES del ejemplo. */
  exampleEs?: string;
}

export interface A0ChoiceRound {
  type: 'choice';
  id: string;
  item: A0RoundItem;
  /** Consigna en español (tap-only). */
  prompt: string;
  /** Glosa correcta (es). */
  answer: string;
  /** Opciones (es) en orden canónico; el front las baraja al renderizar. */
  options: string[];
}

export interface A0RepeatRound {
  type: 'repeat';
  id: string;
  /** Target a repetir. El STT Scribe reconoce si lo dijo (pass/fail); ver POST /a0/repeat-check. */
  phrase: { id: string; en: string; es: string };
  /** URL del audio pregenerado del coach (F3.B); ausente → fallback a TTS on-demand. */
  audioUrl?: string;
}

// ── Forward-compatible: el contrato §13.1 completo. El backend aún NO emite
//    estos tipos (necesitan audio/contenido); las UIs ya están portadas para
//    cuando F3/F4 los sirvan. No aparecen en una sesión de F1b. ─────────────────
export interface A0ListenRound {
  type: 'listen';
  id: string;
  item: A0RoundItem;
  /** Opciones (incluye la correcta); el coach dice `item`, el user toca el significado. */
  options: A0RoundItem[];
  /** URL del audio pregenerado del target (F3.B); ausente → fallback a TTS on-demand. */
  audioUrl?: string;
}

export interface A0MatchRound {
  type: 'match';
  id: string;
  items: A0RoundItem[];
}

export interface A0ClozeRound {
  type: 'cloze';
  id: string;
  item: A0RoundItem;
  sentence: string;
  /** Token correcto que va en el hueco. */
  answer: string;
  options: string[];
}

export interface A0WordBankRound {
  type: 'wordbank';
  id: string;
  item: A0RoundItem;
  bank: { id: string; es: string; answer: string[]; extra: string[] };
}

/**
 * Carta de presentación (pieza B, present-before-test): la PRIMERA ronda de un ítem
 * NUEVO. Presenta el target (audio + EN + glosa ES + ejemplo) sin evaluarlo — NO
 * puntúa al SRS; el examen viene en la ronda de test que la sigue.
 */
export interface A0PresentRound {
  type: 'present';
  id: string;
  item: A0RoundItem;
  /** URL del audio pregenerado del target (F3.B); ausente → fallback a TTS on-demand. */
  audioUrl?: string;
}

/**
 * Disparo de revelado de la escena: el coach EMPIEZA a decir `en` en `tStartMs` del
 * clip. El front une `en` con la fila del board (`a0Content.ts`) para revelarla sync.
 */
export interface A0SceneCue {
  en: string;
  tStartMs: number;
  /**
   * mp3 pregenerado de la palabra (`a0/<slug>.mp3`) — el MISMO audio curado de los
   * ejercicios. Lo usa el re-tap de la fila y el revelado sin clip; ausente → TTS
   * on-demand (factura y suena distinto en cada request).
   */
  audioUrl?: string;
}

/**
 * Presentación con avatar de una familia (plan 34): el motor la inserta al introducir
 * una unit nueva. El coach DICE las palabras (clip con audio) y el board se revela
 * sincronizado por `cues`. Exposición pura → NO puntúa al SRS (la marca `skipped`).
 * `videoUrl` ausente (dev sin Tigris / firma falla) → el front degrada a poster +
 * revelado por timer.
 */
export interface A0SceneRound {
  type: 'scene';
  id: string;
  unit: string;
  titleEs: string;
  videoUrl?: string;
  cues: A0SceneCue[];
}

/**
 * Reacción del coach a la respuesta del alumno (branching determinista, plan 36).
 * Si una opción de `pick` o un `say` la trae, se inserta como una burbuja del coach
 * ANTES de avanzar al turno siguiente — así el coach "escucha" lo que el alumno dijo
 * sin LLM ni red (la rama la elige el dato, no un modelo). Las ramas convergen al
 * mismo cierre para no explotar la autoría.
 */
export interface A0ChatReply {
  /** Línea de reacción del coach en inglés. */
  en: string;
  /** Glosa en español de la reacción. */
  es: string;
}

/**
 * Una opción multiple-choice de un turno del chat. La usan DOS sets:
 *   · `pick` — warm-up de la LECCIÓN (guaranteed-passable, con `reply` por opción).
 *   · `examPick` — el MC del modo EXAMEN (Vía rápida), que GATEA (sin `reply`; ver
 *     `A0ChatTurn.examPick`). En `examPick`, `ok:false` marca un DISTRACTOR del examen
 *     (respuesta conocida pero fuera de lugar); una opción sin `ok` (o `ok:true`) es
 *     correcta. Un turno abierto (sí/no, "¿X o Y?") puede tener 2 correctas.
 */
export interface A0ChatPick {
  /** Texto en inglés que elige el alumno. */
  t: string;
  /** Glosa en español de la opción. */
  es: string;
  /**
   * ¿Es una opción VÁLIDA en la conversación real? Default `true`. Marcá `false`
   * SÓLO para un error inequívoco (ej. "Good night" a la mañana, un distractor del
   * examen) — NUNCA para una respuesta natural que rompe tu guion (declinar una
   * invitación es válido). Ver el authoring prompt: `ok:false` = incorrecto en
   * inglés / fuera de contexto, no "inconveniente para el arco".
   */
  ok?: boolean;
  /** Reacción del coach a ESTA opción (branching). Sólo `pick` (la lección); el examen la ignora. */
  reply?: A0ChatReply;
}

/**
 * Telemetría de un turno MC del chat de cierre en modo EXAMEN (Vía rápida, plan 36 v2.2):
 * qué opción eligió el alumno y si era correcta. Se persiste en `a0_testout_attempts.steps`
 * (jsonb, por-paso) — el chat ya NO pasa por voz, así que su detalle no cae en
 * `a0_voice_attempts`; vive en el por-paso del examen.
 */
export interface A0ChatPickResult {
  /** Línea del coach del turno (la "pregunta"). */
  coach: string;
  /** Texto de la opción que tocó el alumno. */
  chose: string;
  /** ¿La opción elegida era correcta (`ok !== false`)? */
  ok: boolean;
}

/**
 * Respuesta-libre (`say`): el alumno escribe o graba. El match es por CONTENIDO
 * contra `accept` (ver `a0ChatMatch`), no por % de tokens — así "thanks" cuenta
 * como "thank you" y "i want a pizza" NO cuenta como "i want coffee".
 */
export interface A0ChatSay {
  /**
   * Formas aceptables (la canónica + variantes naturales / "carriers" cortos).
   * El alumno pasa si su respuesta CONTIENE (normalizada) alguna de estas formas.
   * Ej.: para agradecer, `['thank you', 'thanks']`; para pedir, `['i eat', 'bread']`.
   * Tupla NO-vacía (≥1 forma): el modo examen de la Vía rápida lee `accept[0]` como la
   * forma canónica para sesgar Scribe — un `accept` vacío es un error de contenido y el
   * tipo lo rechaza en compile-time (fail-fast, sin `!` que mienta sobre la invariante).
   */
  accept: [string, ...string[]];
  /** Reacción genérica del coach, válida para CUALQUIER `accept`. Opcional. */
  reply?: A0ChatReply;
}

/**
 * Un turno del chat por guion (plan 35, branching en plan 36): el coach habla
 * (EN + glosa ES) y el alumno responde de UNA de dos formas (excluyentes):
 *   · `pick` — multiple-choice (warm-up): toca una opción. Cada opción puede traer
 *     su `reply` (el coach reacciona distinto a cada elección).
 *   · `say`  — respuesta-libre: escribe o graba; match por contenido contra
 *     `say.accept`. `help` revela respuestas predeterminadas (botón AYUDA).
 * Un turno trae `pick` XOR `say`; el AUTOR del guion elige el modo (el `support`
 * sólo afina cuánta muleta se ve, no el modo).
 */
export interface A0ChatTurn {
  /** Línea del coach en inglés (lo que el alumno escucha/lee). */
  coach: string;
  /** Glosa en español de la línea del coach (andamiaje, gobernado por `support`). */
  coachEs: string;
  /** Warm-up multiple-choice: opciones (cada una con su reacción opcional). */
  pick?: A0ChatPick[];
  /** Respuesta-libre: formas aceptables + reacción del coach. */
  say?: A0ChatSay;
  /** Respuestas predeterminadas que revela el botón de AYUDA (sólo modo `say`). */
  help?: { en: string; es: string }[];
  /**
   * MC del modo EXAMEN (Vía rápida, plan 36 v2.2) — SÓLO lo usa el chat de cierre del
   * test-out (`A0ChatScene` con `exam`). EXACTAMENTE 4 opciones que GATEAN: el alumno
   * toca una y el turno cuenta OK si eligió una correcta (`ok !== false`). Independiente
   * de `pick`/`say` (que son la LECCIÓN, intacta): un turno que en la lección es `say`
   * (respuesta libre por voz/texto) en el examen se responde por este MC determinista.
   * Las units sin guion (a0u4–11) no lo necesitan — caen al MC genérico (`A0TestOutChat`).
   * Invariante (testeada en `tests/unit/a0ChatScripts.test.ts`): todo turno de todo guion
   * trae `examPick` con 4 opciones y ≥1 correcta.
   */
  examPick?: A0ChatPick[];
}

/**
 * Charla por guion (plan 35): una conversación FIJA, client-side, que reemplaza
 * la micro-charla en vivo del medio del sendero. Es exposición pura → NO puntúa
 * al SRS (el shell la marca `skipped`, igual que `scene`/`present`). El backend
 * manda sólo el esqueleto (`scriptId`); el cliente resuelve el guion (turnos +
 * título + unit) desde `a0ChatScripts.ts` (mismo patrón con que `scene` resuelve
 * el board de `a0Content.ts`).
 */
export interface A0ChatRound {
  type: 'chat';
  id: string;
  /** Id del guion autorado → `a0ChatScripts.ts` (de ahí salen turnos/título/unit). */
  scriptId: string;
}

export type A0Round =
  | A0ChoiceRound
  | A0RepeatRound
  | A0ListenRound
  | A0MatchRound
  | A0ClozeRound
  | A0WordBankRound
  | A0PresentRound
  | A0SceneRound
  | A0ChatRound;

export interface A0Session {
  sessionId: string;
  support: A0Support;
  rounds: A0Round[];
}

/** Etapa de andamiaje derivada del % de ítems dominados (full/medium/light). */
export type A0Stage = 'full' | 'medium' | 'light';

/** Estado + maestría de una unit del corpus (derivado de `a0_item_state`, §4.4). */
export interface A0UnitMastery {
  /** Id estable de la unit (`a0u1` · `a0u2` · `a0u3`). */
  id: string;
  state: 'todo' | 'doing' | 'done';
  /** 0..1 — progreso GRADUADO de la unit (esfuerzo, no sólo box≥3) → la barra se
   *  mueve desde la 1ª práctica. `state==='done'` sí es dominio real (box≥3 ≥80%). */
  mastery: number;
  /** Conteo ENTERO de palabras dominadas (box≥3) de la unit — el Hito muestra esto. */
  mastered: number;
  /** Total de ítems de la unit. */
  size: number;
}

/**
 * Progreso del A0 para el Mapa/Home (§13.1, F2). TODO derivado de la maestría
 * por ítem: el `state`/`mastery` por unit, el medidor de graduación
 * (`have`/`need` = ítems dominados / total) y si el boss está listo. Cierra el
 * feedback "completar no se marca".
 */
/**
 * Clips de avatar de los coaches del A0 (plan 34), firmados por el backend desde
 * Tigris. `{ <coachId>: { greeting, present, celebrate } }` con URLs firmadas.
 * Ausente (sin signer / dev sin Tigris) → el front cae al poster estático.
 */
export type A0CoachClipsDto = Record<string, Partial<Record<'greeting' | 'present' | 'celebrate', string>>>;

export interface A0Overview {
  support: A0Support;
  a0Stage: A0Stage;
  units: A0UnitMastery[];
  gradMeter: { have: number; need: number };
  bossReady: boolean;
  /**
   * ¿Ofrecer el test-out ("Vía rápida")? El server lo enciende recién cuando el
   * user demostró que el A0 básico le queda chico (precisión ≥80% sobre lo jugado) y
   * todavía está temprano — así el banner NO le aparece a un cero real (plan 36).
   */
  testOutEligible: boolean;
  /** Clips firmados del avatar por coach × momento (plan 34). Ausente → poster-only. */
  coachClips?: A0CoachClipsDto;
}

/** Resultado de una palabra jugada, listo para el write-back al SRS. */
export interface A0RunItem {
  /** Texto EN del target (se normaliza server-side → cruza con `a0_item_state.item_id`). */
  text: string;
  ok: boolean;
  /**
   * Tipo de ronda con que se jugó (motor de tiers, plan 36). El server lo mapea al
   * TIER que ejercita: choice/listen→reconocer, match→recordar, cloze/wordbank/
   * repeat→producir/usar. Mueve `tier`/`consolidated`. Lo setea `a0RoundWriteBack`.
   */
  roundType?: A0RoundType;
}

/**
 * Pide la sesión de rondas A0 al selector server-side. `limit` se clampa 1..50
 * en el backend (default 12).
 */
export function getA0Session(limit?: number): Promise<A0Session> {
  const qs = typeof limit === 'number' ? `?limit=${limit}` : '';
  return jsonFetch<A0Session>(`/a0/session${qs}`);
}

/**
 * Progreso del A0 (units con state/mastery + medidor de graduación), derivado
 * server-side de la maestría por ítem. Lo consumen el Home y el Mapa (F2).
 */
export function getA0Overview(): Promise<A0Overview> {
  return jsonFetch<A0Overview>('/a0/overview');
}

/**
 * Gradúa al A0 a A1 (boss-fight, F4). El server re-valida la maestría
 * (`bossReady`) — no se puede saltear el gate desde el cliente; un 403 significa
 * que todavía no alcanzó la evidencia. Idempotente (ya A1 → ok). Tras esto, el
 * Home completo se revela (gate `cefrGlobal!=='A0'`).
 */
export function graduateA0(): Promise<{ cefrGlobal: string; alreadyGraduated?: boolean }> {
  return jsonFetch<{ cefrGlobal: string; alreadyGraduated?: boolean }>('/a0/graduate', {
    method: 'POST',
  });
}

/**
 * Marca la charla-misión del boss como completada (plan 33 §1.3) → el server
 * setea `bossPassed=true`, habilitando la mitad "pasar" del gate de graduación.
 * Lo llama la charla cuando se cumplieron todos los objetivos del boss (la red
 * garantiza pasar; el server NO re-juzga). Idempotente. Tras esto, el motor A0
 * dispara `graduateA0` (ahora pasa: maestría + bossPassed) → ceremonia → reveal.
 */
export function recordA0BossResult(
  missionId: string,
): Promise<{ bossPassed: boolean }> {
  return jsonFetch<{ bossPassed: boolean }>('/a0/boss-result', {
    method: 'POST',
    body: JSON.stringify({ missionId, completed: true }),
  });
}

/**
 * Marca un chat por guion (warm-up, plan 35) como visto → el backend lo registra en
 * `a0_chat_seen` para que NO reaparezca (una vez por chat). Lo llama el shell de
 * lección cuando una ronda `chat` se completa. Idempotente; si falla, el chat
 * simplemente reaparece en la próxima sesión (no rompe el flujo).
 */
export function recordA0ChatSeen(scriptId: string): Promise<{ scriptId: string }> {
  return jsonFetch<{ scriptId: string }>('/a0/chat-seen', {
    method: 'POST',
    body: JSON.stringify({ scriptId }),
  });
}

/**
 * Registra la lección como una partida de práctica → el backend puebla
 * `a0_item_state` por ítem (Leitner: acierto sube de caja, fallo cae a la 0 y
 * reprograma due=ahora). `runId` (UUID) hace el POST idempotente.
 */
export function recordA0Run(input: {
  runId: string;
  sessionId: string;
  items: A0RunItem[];
}): Promise<{ runId: string; credited: number }> {
  return jsonFetch<{ runId: string; credited: number }>('/practice-runs', {
    method: 'POST',
    body: JSON.stringify({
      runId: input.runId,
      game: 'a0-lesson',
      deck: `a0:${input.sessionId}`,
      items: input.items,
    }),
  });
}

// ─── Test-out por producción a nivel unidad (modo "Vía rápida", plan 36 §4.3) ──

/** Ítem de un desafío de test-out: glosa ES → el alumno DICE el inglés en frío. */
export interface A0TestOutItem {
  /** item_id normalizado (cruza con a0_item_state.item_id). */
  id: string;
  en: string;
  es: string;
}

/**
 * Un paso del examen mixto de la Vía rápida (plan 36 v2). El server arma un set fijo:
 * `match` (unir EN↔ES, variedad) + `produce` (producción por voz en frío) + un `chat`
 * de cierre que el cliente resuelve (genérico desde estos ítems, o autorado más adelante).
 */
export type A0TestOutStep =
  | { kind: 'match'; items: A0TestOutItem[] }
  | { kind: 'produce'; item: A0TestOutItem }
  // Chat de cierre MC. `item` = la opción CORRECTA (el server elige uno que el examen NO tocó;
  // ausente sólo en units muy chicas → el front cae a un ítem visto). `distractors` = las
  // opciones INCORRECTAS del MC genérico (otros ítems de la unit, glosa ES distinta), que el
  // server provee para GARANTIZAR 4 opciones que gatean aun en un examen de 1 solo paso. Los
  // guiones autorados (a0u1/2/3) usan su propio `examPick` e IGNORAN estos campos.
  | { kind: 'chat'; item?: A0TestOutItem; distractors?: A0TestOutItem[] };

/**
 * Examen de la PRÓXIMA unidad testeable (la 1ª con ≥5 ítems aún no afianzados), o
 * `unit: null` si ya no queda nada para saltear (todo afianzado / resto chico).
 */
export interface A0TestOutChallenge {
  unit: { id: string; name: string; steps: A0TestOutStep[] } | null;
}

/** Resultado por paso que el cliente reporta al cerrar el examen (POST). */
export type A0TestOutStepResult =
  | { kind: 'match'; ok: boolean }
  | { kind: 'produce'; text: string; ok: boolean }
  // `texts` queda `[]` (el chat MC ya no afianza por voz); `picks` = telemetría por-turno
  // (qué eligió vs si era correcta) que el server persiste en `a0_testout_attempts.steps`.
  | { kind: 'chat'; ok: boolean; texts: string[]; picks?: A0ChatPickResult[] };

/** Resultado del POST: ¿la unidad testeó out (≥80%)? + cuántos ítems quedaron afianzados. */
export interface A0TestOutResult {
  /** ≥A0_TESTOUT_MIN_ITEMS resultados y ≥80% OK → "saltada" + ofrecer la próxima. */
  passed: boolean;
  /** Ítems afianzados al instante (los producidos OK que pertenecen a la unit). */
  consolidatedCount: number;
  /**
   * ¿La unidad quedó COMPLETA (≥80% dominada por box) tras afianzar? Para la entrada por
   * popover a un tema casi cerrado (<mínimo pendientes): no "testea out" por la regla de
   * ≥mínimo resultados, pero si clavó lo que faltaba igual se completa → la UX celebra con
   * `passed || unitDone`.
   */
  unitDone: boolean;
}

/**
 * Trae el desafío de la próxima unidad testeable (modo "Vía rápida"). El que YA sabe
 * demuestra produciendo en frío y saltea temas enteros. `unit:null` = no queda nada
 * para saltear. `exclude` = unit ids ya intentadas esta sentada → el greedy las saltea
 * (no re-ofrece la unit recién fallada). `unit` = pedir UNA unidad puntual (entrada por
 * el popover de un nodo): el server devuelve sólo esa unit (o `null` si ya casi cerrada)
 * en vez de ir greedy por orden de corpus.
 */
export function getA0TestOut(
  exclude: readonly string[] = [],
  unit?: string,
): Promise<A0TestOutChallenge> {
  const params = new URLSearchParams();
  if (exclude.length > 0) params.set('exclude', exclude.join(','));
  if (unit) params.set('unit', unit);
  const qs = params.toString();
  return jsonFetch<A0TestOutChallenge>(`/a0/test-out${qs ? `?${qs}` : ''}`);
}

/**
 * Cierra el examen mixto: manda el resultado por paso (`match`/`produce`/`chat`). El
 * backend calcula el veredicto (≥80% de pasos OK Y chat OK), afianza la unidad ENTERA si
 * aprobó (o sólo lo producido OK por voz si no) y dice si la saltó. Idempotente de facto.
 */
export function submitA0TestOut(
  unitId: string,
  steps: A0TestOutStepResult[],
): Promise<A0TestOutResult> {
  return jsonFetch<A0TestOutResult>('/a0/test-out', {
    method: 'POST',
    body: JSON.stringify({ unitId, steps }),
  });
}

/** Resultado del gate de pronunciación del repeat-after A0 (STT, no GOP). */
export interface A0RepeatCheck {
  /** ¿El target se reconoció en lo transcripto? (inteligibilidad, no nativeness). */
  pass: boolean;
  /** Lo que el STT oyó (normalizado) — útil para debug, no se le muestra al A0. */
  heard: string;
  /** Fracción de tokens del target reconocidos (0..1) — señal, no score 0..100. */
  coverage: number;
}

/**
 * Gate de pronunciación del repeat-after A0 por STT (ElevenLabs Scribe). Sube el
 * clip WAV (multipart) → el backend transcribe y matchea contra el target de
 * forma LAXA: medimos INTELIGIBILIDAD (¿se reconoce el target?), NO nativeness
 * (eso es phonos/GOP, que queda para A2+). `pass=true` = el target se reconoció.
 * Un no-2xx (STT caído / sin key) TIRA → el front cae a "no pudimos puntuar /
 * reintentar". No usa el `jsonFetch` de arriba porque ése fuerza
 * `content-type: application/json`, incompatible con el multipart de FormData.
 */
export async function checkA0Repeat(
  word: string,
  audio: Blob,
  opts?: { synonyms?: boolean; context?: string; unit?: string },
): Promise<A0RepeatCheck> {
  const fd = new FormData();
  fd.append('word', word);
  fd.append('audio', audio, 'repeat.wav');
  // `synonyms` (sólo Vía rápida): acepta cualquier forma del corpus con la MISMA glosa ES
  // (decís "hi" o "hello" para "hola"). La lección normal (repetir tras el coach) NO lo manda.
  if (opts?.synonyms) fd.append('synonyms', '1');
  // `context`/`unit`: atribución para la telemetría (a0_voice_attempts) — de qué flujo
  // ('lesson' | 'testout-produce' | 'testout-chat') y unit viene el intento. Opcionales.
  if (opts?.context) fd.append('context', opts.context);
  if (opts?.unit) fd.append('unit', opts.unit);
  // FormData setea su propio content-type (con boundary): no lo forzamos.
  let res: Response;
  try {
    res = await apiFetch(`${BASE}/a0/repeat-check`, { method: 'POST', body: fd });
  } catch {
    throw new Error('No pudimos conectar con el servidor.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  const json = (await res.json()) as { data?: A0RepeatCheck } & A0RepeatCheck;
  return json.data ?? json;
}

/**
 * Targets de write-back de una ronda según su resultado. El SRS se mueve por
 * ÍTEM: choice/repeat/listen/cloze/wordbank son 1 ítem; `match` desglosa
 * N (uno por par, `ok` = no estuvo entre los fallados). El texto EN normaliza
 * al `item_id`. El `repeat` ya NO trae score (Scribe da inteligibilidad, no GOP
 * 0..100): el SRS se mueve sólo por `ok`. La `present` NO escribe (es exposición,
 * no examen — además el shell la marca `skipped` y ni llama acá). Puro → testeable.
 */
export function a0RoundWriteBack(
  round: A0Round,
  ok: boolean,
  opts: { missed?: readonly string[] } = {},
): A0RunItem[] {
  switch (round.type) {
    case 'present':
    case 'scene':
    case 'chat':
      // Exposición pura (plan 35, decisión #2): el chat practica fluidez, no
      // examina vocabulario → no escribe el SRS ni acelera `bossReady`.
      return [];
    case 'choice':
    case 'listen':
    case 'cloze':
    case 'wordbank':
      return [{ text: round.item.en, ok, roundType: round.type }];
    case 'repeat':
      return [{ text: round.phrase.en, ok, roundType: round.type }];
    case 'match':
      return round.items.map((it) => ({
        text: it.en,
        ok: !(opts.missed ?? []).includes(it.id),
        roundType: round.type,
      }));
  }
}
